import axios from 'axios';
import various from '../General/various';

const TIMEOUT_MS = 15000;
const editableFields = ['title', 'slug', 'excerpt', 'body', 'status', 'tag_ids'];
const validTagIds = (ids) => Array.isArray(ids) && ids.length <= 100
  && ids.every((id) => Number.isSafeInteger(id) && id > 0) && new Set(ids).size === ids.length;
const validAuthorKey = (key) => typeof key === 'string' && /^[a-f0-9]{64}$/i.test(key);

export const getBlogSessionToken = () => {
  try {
    return window.localStorage.getItem('accesstoken') || '';
  } catch (error) {
    return '';
  }
};

const failure = (kind, status = null, fields = []) => Object.assign(
  new Error('TOUZEN_BLOG_REQUEST_FAILED'), { kind, status, fields }
);
const normalizeError = (error) => {
  if (error?.kind) return error;
  const status = error?.response?.status || null;
  if (status === 401) return failure('unauthenticated', status);
  if (status === 403) return failure('forbidden', status);
  if (status === 404) return failure('not_found', status);
  if (status === 400) {
    const data = error.response?.data;
    return failure('validation', status, [...editableFields, 'cover_image', 'name', 'display_name', 'public_name'].filter((field) => data && Object.prototype.hasOwnProperty.call(data, field)));
  }
  if (error?.code === 'ECONNABORTED' || error?.code === 'ETIMEDOUT') return failure('timeout');
  if (error?.code === 'ERR_CANCELED') return failure('canceled');
  if (error?.code === 'ERR_NETWORK') return failure('network');
  return failure('unknown', status);
};

export const blogErrorMessage = (error, saving = false) => {
  const messages = {
    unauthenticated: 'ログインの有効期限が切れたか、刀禅の認証を確認できません。ログインし直してください。',
    forbidden: 'この記事を操作する権限がありません。',
    not_found: '記事が見つからないか、公開されていません。',
    validation: '入力内容を確認してください。',
    timeout: '通信が時間内に完了しませんでした。',
    network: '通信できませんでした。接続を確認してください。',
    session_changed: 'ログイン状態が変わりました。画面を開き直してください。',
  };
  const message = messages[error?.kind] || '処理を完了できませんでした。しばらくしてからお試しください。';
  return saving && ['timeout', 'network', 'unknown', 'session_changed', 'invalid_response'].includes(error?.kind)
    ? `${message} 保存結果を確認できていません。記事一覧で状態を確認してから再操作してください。`
    : message;
};

async function request(method, path, { data, signal, authenticated = false, expectedToken, publicRead = false } = {}) {
  const token = authenticated ? getBlogSessionToken() : '';
  if (authenticated && expectedToken !== undefined && expectedToken !== token) throw failure('session_changed');
  if (authenticated && !token) throw failure('unauthenticated', 401);
  try {
    // Isolate Blog failures from the legacy global authentication interceptors.
    const client = publicRead ? axios.create({ headers: { common: { Authorization: null } }, auth: null }) : axios.create();
    const response = await client.request({
      method,
      url: `${various.currentDomain.replace(/\/?$/, '/')}blog/api/${path}`,
      timeout: TIMEOUT_MS,
      signal,
      ...(publicRead ? { adapter: 'fetch', withCredentials: false, withXSRFToken: false, auth: null, fetchOptions: { cache: 'no-store' } } : {}),
      headers: {
        ...(data instanceof FormData ? {} : { 'Content-Type': 'application/json' }),
        ...(authenticated ? { Authorization: `Bearer ${token}` } : publicRead ? { Authorization: null } : {}),
      },
      ...(data === undefined ? {} : { data }),
    });
    if (authenticated && getBlogSessionToken() !== token) throw failure('session_changed');
    return response.data;
  } catch (error) {
    if (authenticated && getBlogSessionToken() !== token) throw failure('session_changed');
    throw normalizeError(error);
  }
}

export async function getBlogReleasePolicy(signal, { authenticated = false, expectedToken } = {}) {
  const data = await request('get', 'release/policy/', { signal, authenticated, expectedToken, publicRead: true });
  if (!data || Object.keys(data).sort().join(',') !== 'can_manage,public_release'
    || typeof data.public_release !== 'boolean' || typeof data.can_manage !== 'boolean' || (!authenticated && data.can_manage)) throw failure('invalid_response');
  return { public_release: data.public_release, can_manage: data.can_manage };
}

const requireList = (data) => {
  if (!Array.isArray(data?.results)) throw failure('invalid_response');
  return data.results;
};

const validLikeOperation = (id) => typeof id === 'string' && id.length === 32 && /^[a-f0-9]{32}$/.test(id);
const requireLike = (data, mode, actor) => {
  const keys = mode === 'status' ? ['actor_kind', 'count', 'liked', 'operation_status'] : ['actor_kind', 'count', 'liked'];
  if (!data || Object.keys(data).sort().join(',') !== keys.join(',')
    || !Number.isSafeInteger(data.count) || data.count < 0 || !['member', 'guest'].includes(data.actor_kind)
    || (mode === 'get' && data.actor_kind === 'guest' ? data.liked !== null : mode === 'status'
      ? (data.liked !== null && typeof data.liked !== 'boolean') : typeof data.liked !== 'boolean')) throw failure('invalid_response');
  if (actor && data.actor_kind !== actor) throw failure('actor_changed');
  if (mode === 'status' && (data.actor_kind !== 'guest' || !['confirmed', 'pending', 'unknown'].includes(data.operation_status)
    || (data.operation_status === 'confirmed' && typeof data.liked !== 'boolean'))) throw failure('invalid_response');
  return { count: data.count, liked: data.liked, actor_kind: data.actor_kind,
    ...(mode === 'status' ? { operation_status: data.operation_status } : {}) };
};
async function likeRequest(slug, data, { signal, authenticated = false, expectedToken } = {}) {
  const token = authenticated ? getBlogSessionToken() : '';
  if (authenticated && !token) throw failure('unauthenticated', 401);
  if (authenticated && expectedToken !== undefined && expectedToken !== token) throw failure('session_changed');
  try {
    // Fetch maps false to credentials:omit; null suppresses inherited auth headers.
    const response = await axios.create({ headers: { common: { Authorization: null } }, auth: null }).request({
      method: data ? 'post' : 'get', url: `${various.currentDomain.replace(/\/?$/, '/')}blog/api/${encodeURIComponent(slug)}/like/`,
      adapter: 'fetch', withCredentials: false, withXSRFToken: false, auth: null, timeout: TIMEOUT_MS, signal,
      headers: { 'Content-Type': 'application/json', Authorization: authenticated ? `Bearer ${token}` : null },
      ...(data ? { data } : {}),
    });
    if (authenticated && getBlogSessionToken() !== token) throw failure('session_changed');
    return response.data;
  } catch (error) {
    if (authenticated && getBlogSessionToken() !== token) throw failure('session_changed');
    throw normalizeError(error);
  }
}
export const getBlogLike = async (slug, options) => requireLike(await likeRequest(slug, undefined, options), 'get');
export const setBlogLike = async (slug, input, options) => {
  if (!input || !['member', 'guest'].includes(input.actor_kind) || typeof input.liked !== 'boolean'
    || (input.actor_kind === 'guest' && (!validLikeOperation(input.operation_id) || typeof input.previously_liked !== 'boolean'))) throw failure('validation', 400);
  const data = { action: 'set', liked: input.liked, ...(input.actor_kind === 'guest'
    ? { previously_liked: input.previously_liked, operation_id: input.operation_id } : {}) };
  return requireLike(await likeRequest(slug, data, options), 'set', input.actor_kind);
};
export const getBlogLikeOperation = async (slug, operationId, options) => {
  if (!validLikeOperation(operationId)) throw failure('validation', 400);
  return requireLike(await likeRequest(slug, { action: 'status', operation_id: operationId }, options), 'status', 'guest');
};

// Best-effort operation counts; this never confirms an external post or send.
export const recordBlogShareOperation = async (slug, channel) => {
  if (typeof slug !== 'string' || !slug || slug.length > 255 || !['x', 'facebook', 'mail', 'copy'].includes(channel)) {
    return { confirmed: false, reason: 'validation' };
  }
  let operationId;
  try {
    const bytes = new Uint8Array(16);
    window.crypto.getRandomValues(bytes);
    operationId = Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
  } catch (error) { return { confirmed: false, reason: 'unsupported' }; }
  try {
    const response = await axios.create({ headers: { common: { Authorization: null } }, auth: null }).request({
      method: 'post', url: `${various.currentDomain.replace(/\/?$/, '/')}blog/api/${encodeURIComponent(slug)}/share/`,
      adapter: 'fetch', withCredentials: false, withXSRFToken: false, auth: null, timeout: TIMEOUT_MS,
      fetchOptions: { keepalive: true },
      headers: { 'Content-Type': 'application/json', Authorization: null },
      data: { channel, operation_id: operationId },
    });
    if (!response.data || Object.keys(response.data).join(',') !== 'recorded' || response.data.recorded !== true) {
      return { confirmed: false, reason: 'invalid_response' };
    }
    return { confirmed: true };
  } catch (error) { return { confirmed: false, reason: normalizeError(error).kind }; }
};

const requirePublicIdentity = (data) => {
  if (['tag_ids', 'author', 'author_id', 'user', 'user_id', 'email'].some((field) => Object.prototype.hasOwnProperty.call(data, field))) throw failure('invalid_response');
  const tags = Object.prototype.hasOwnProperty.call(data, 'tags') ? data.tags : [];
  const key = Object.prototype.hasOwnProperty.call(data, 'author_key') ? data.author_key : null;
  if (!Array.isArray(tags) || (key !== null && !validAuthorKey(key))) throw failure('invalid_response');
  const normalized = tags.map(requireBlogTag);
  if (new Set(normalized.map((tag) => tag.id)).size !== normalized.length) throw failure('invalid_response');
  return { ...data, tags: normalized, author_key: key === null ? null : key.toLowerCase() };
};
const requireArticle = (data, managed = false) => {
  if (!data || ['title', 'slug', 'body', 'author_display_name'].some((field) => typeof data[field] !== 'string')
    || (data.excerpt !== undefined && typeof data.excerpt !== 'string')
    || (managed && (!Number.isInteger(data.id) || !['draft', 'published'].includes(data.status)
      || typeof data.is_publication_blocked !== 'boolean' || !validTagIds(data.tag_ids)
      || typeof data.is_own_post !== 'boolean' || (data.author_key !== null && !validAuthorKey(data.author_key))))) throw failure('invalid_response');
  const counts = {};
  let tags = [];
  if (managed) {
    ['like_count', 'share_operation_count', 'comment_count'].forEach((field) => {
      const present = Object.prototype.hasOwnProperty.call(data, field);
      if (present && (!Number.isSafeInteger(data[field]) || data[field] < 0)) throw failure('invalid_response');
      counts[field] = present ? data[field] : null;
    });
    if (Object.prototype.hasOwnProperty.call(data, 'tags')) {
      if (!Array.isArray(data.tags)) throw failure('invalid_response');
      tags = data.tags.map(requireBlogTag);
      if (new Set(tags.map((tag) => tag.id)).size !== tags.length || tags.length !== data.tag_ids.length
        || tags.some((tag) => !data.tag_ids.includes(tag.id))) throw failure('invalid_response');
    }
  }
  const article = managed ? { ...data, ...counts, tags } : requirePublicIdentity(data);
  const imageField = managed ? 'cover_image' : 'cover_image_url';
  if (typeof data[imageField] === 'string' && data[imageField]) {
    try {
      const imageUrl = new URL(data[imageField], various.currentDomain);
      if (!['http:', 'https:'].includes(imageUrl.protocol)) throw failure('invalid_response');
      return { ...article, [imageField]: imageUrl.href };
    } catch (error) { throw failure('invalid_response'); }
  }
  return article;
};

export const getBlogCapabilities = async (signal) => {
  const data = await request('get', 'manage/capabilities/', { signal, authenticated: true });
  if (typeof data?.can_manage !== 'boolean' || typeof data?.can_moderate !== 'boolean'
    || (data.can_moderate && !data.can_manage)) throw failure('invalid_response');
  return data;
};
const requirePublicProfile = (data) => {
  if (typeof data?.display_name !== 'string' || Array.from(data.display_name).length > 255
    || (data.author_key !== null && !validAuthorKey(data.author_key))
    || ['can_edit_public_name', 'can_manage_blog', 'is_member'].some((field) => typeof data[field] !== 'boolean')) throw failure('invalid_response');
  return { display_name: data.display_name, author_key: data.author_key === null ? null : data.author_key.toLowerCase(),
    can_edit_public_name: data.can_edit_public_name, can_manage_blog: data.can_manage_blog, is_member: data.is_member };
};
export const getBlogPublicProfile = async (signal) => requirePublicProfile(
  await request('get', 'profile/self/', { signal, authenticated: true })
);
export const initializeBlogPublicProfile = async (signal, expectedToken) => requirePublicProfile(
  await request('post', 'profile/self/initialize/', { data: {}, signal, authenticated: true, expectedToken })
);
export const saveBlogPublicProfile = async (displayName, signal) => {
  if (typeof displayName !== 'string' || !displayName.trim() || Array.from(displayName.trim()).length > 255) throw failure('validation', 400, ['display_name']);
  return requirePublicProfile(await request('patch', 'profile/self/', { data: { display_name: displayName }, signal, authenticated: true }));
};
const requirePublicPreview = (data) => {
  if (!data || ['title', 'slug', 'author_display_name', 'body_preview'].some((field) => typeof data[field] !== 'string')
    || typeof data.excerpt !== 'string' || Object.prototype.hasOwnProperty.call(data, 'body')) throw failure('invalid_response');
  const preview = requirePublicIdentity(data);
  if (typeof data.cover_image_url === 'string' && data.cover_image_url) {
    try {
      const imageUrl = new URL(data.cover_image_url, various.currentDomain);
      if (!['http:', 'https:'].includes(imageUrl.protocol)) throw failure('invalid_response');
      return { ...preview, cover_image_url: imageUrl.href };
    } catch (error) { throw failure('invalid_response'); }
  }
  return preview;
};
export const listPublicBlogPosts = async (signal, page = 1, filters = {}, options = {}) => {
  const requestedPage = Number.isSafeInteger(page) && page > 0 ? page : 1;
  const query = new URLSearchParams({ page: String(requestedPage) });
  if (filters.tag !== undefined) {
    if (!Number.isSafeInteger(filters.tag) || filters.tag < 1) throw failure('validation', 400);
    query.set('tag', String(filters.tag));
  }
  if (filters.author !== undefined) {
    if (!validAuthorKey(filters.author)) throw failure('validation', 400);
    query.set('author', filters.author.toLowerCase());
  }
  const data = await request('get', `?${query}`, { ...options, signal, publicRead: true });
  if (!Number.isSafeInteger(data?.count) || data.count < 0 || data.page_size !== 10
    || !Number.isSafeInteger(data.page) || data.page < 1 || !Number.isSafeInteger(data.total_pages)
    || data.total_pages !== Math.max(1, Math.ceil(data.count / 10))
    || data.page !== Math.min(requestedPage, data.total_pages) || !Array.isArray(data.results)
    || data.results.length !== Math.min(10, data.count - (data.page - 1) * 10)
    || data.results.some((post) => !Number.isSafeInteger(post?.comment_count) || post.comment_count < 0)) throw failure('invalid_response');
  return { count: data.count, page: data.page, page_size: 10, total_pages: data.total_pages,
    results: data.results.map(requirePublicPreview) };
};
export const getPublicBlogPost = async (slug, signal) => requireArticle(
  await request('get', `${encodeURIComponent(slug)}/`, { signal })
);
export const getPublicBlogNavigation = async (slug, signal, filters = {}, options = {}) => {
  if (typeof slug !== 'string' || !slug.trim()) throw failure('validation', 400);
  const query = new URLSearchParams();
  if (filters.tag !== undefined) {
    if (!Number.isSafeInteger(filters.tag) || filters.tag < 1) throw failure('validation', 400);
    query.set('tag', String(filters.tag));
  }
  if (filters.author !== undefined) {
    if (!validAuthorKey(filters.author)) throw failure('validation', 400);
    query.set('author', filters.author.toLowerCase());
  }
  const data = await request('get', `${encodeURIComponent(slug)}/navigation/${query.toString() ? `?${query}` : ''}`, { ...options, signal, publicRead: true });
  if (!data || Object.keys(data).sort().join(',') !== 'next,previous') throw failure('invalid_response');
  for (const post of [data.previous, data.next]) {
    if (post !== null && (!post || Object.keys(post).sort().join(',') !== 'slug,title'
      || typeof post.title !== 'string' || !post.title.trim() || typeof post.slug !== 'string' || !post.slug.trim() || post.slug === slug)) throw failure('invalid_response');
  }
  if (data.previous && data.next && data.previous.slug === data.next.slug) throw failure('invalid_response');
  return data;
};
export const listRelatedBlogPosts = async (slug, signal) => {
  const results = requireList(await request('get', `${encodeURIComponent(slug)}/related/`, { signal }));
  if (results.length > 8 || results.some((post) => post?.slug === slug)
    || new Set(results.map((post) => post?.slug)).size !== results.length) throw failure('invalid_response');
  return results.map(requirePublicPreview);
};
const requireBlogTag = (data) => {
  if (!Number.isSafeInteger(data?.id) || data.id < 1 || typeof data.name !== 'string'
    || !data.name.trim() || Array.from(data.name).length > 50) throw failure('invalid_response');
  return { id: data.id, name: data.name };
};
export const listPublicBlogFilters = async (signal, options = {}) => {
  const data = await request('get', 'filters/options/', { ...options, signal, publicRead: true });
  if (!Array.isArray(data?.tags) || !Array.isArray(data.authors)) throw failure('invalid_response');
  const tags = data.tags.map(requireBlogTag);
  const authors = data.authors.map((author) => {
    if (!validAuthorKey(author?.key) || typeof author.name !== 'string' || !author.name.trim()) throw failure('invalid_response');
    return { key: author.key.toLowerCase(), name: author.name };
  });
  if (new Set(tags.map((tag) => tag.id)).size !== tags.length || new Set(authors.map((author) => author.key)).size !== authors.length) throw failure('invalid_response');
  return { tags, authors };
};
export const listBlogTags = async (signal) => {
  const tags = requireList(await request('get', 'manage/tags/', { signal, authenticated: true })).map(requireBlogTag);
  if (new Set(tags.map((tag) => tag.id)).size !== tags.length) throw failure('invalid_response');
  return tags;
};
// Match Python's NFKC + str.split whitespace contract without changing the submitted name.
export const normalizeNewBlogTagName = (name) => typeof name === 'string'
  // eslint-disable-next-line no-control-regex
  ? name.normalize('NFKC').split(/[\p{White_Space}\u001c-\u001f]+/u).filter(Boolean).join(' ') : '';
export const createBlogTag = async (name, signal) => {
  const normalized = normalizeNewBlogTagName(name);
  if (!normalized || Array.from(normalized).length > 15) throw failure('validation', 400, ['name']);
  const tag = requireBlogTag(await request('post', 'manage/tags/', { data: { name }, signal, authenticated: true }));
  // Creation can reuse an older tag through its casefold key; responses retain the read contract.
  if (tag.name !== normalizeNewBlogTagName(tag.name)) throw failure('invalid_response');
  return tag;
};
export const listManagedBlogPosts = async (signal, options = {}) => {
  const page = Number.isSafeInteger(options.page) && options.page > 0 ? options.page : 1;
  const q = options.q === undefined ? '' : options.q;
  const status = options.status === undefined ? '' : options.status;
  const sort = options.sort === undefined ? 'updated_desc' : options.sort;
  const tab = options.tab === undefined ? 'active' : options.tab;
  if (typeof q !== 'string' || Array.from(q.trim()).length > 200
    || !['active', 'archive'].includes(tab)
    || !['', 'draft', 'published', 'blocked'].includes(status)
    || !['updated_desc', 'updated_asc', 'published_desc', 'title_asc', 'likes_desc', 'shares_desc', 'author_asc', 'author_desc'].includes(sort)
    || (options.tag !== undefined && (!Number.isSafeInteger(options.tag) || options.tag < 1))) throw failure('validation', 400);
  const query = new URLSearchParams({ page: String(page), sort, tab });
  if (q.trim()) query.set('q', q.trim());
  if (status) query.set('status', status);
  if (options.tag !== undefined) query.set('tag', String(options.tag));
  const data = await request('get', `manage/posts/?${query}`, { signal, authenticated: true });
  if (!Number.isSafeInteger(data?.count) || data.count < 0 || data.page_size !== 10
    || !Number.isSafeInteger(data.page) || data.page < 1 || !Number.isSafeInteger(data.total_pages)
    || data.total_pages !== Math.max(1, Math.ceil(data.count / 10)) || data.page !== Math.min(page, data.total_pages)
    || !Array.isArray(data.results) || data.results.length !== Math.min(10, data.count - (data.page - 1) * 10)) throw failure('invalid_response');
  return { count: data.count, page: data.page, page_size: 10, total_pages: data.total_pages, results: data.results.map((post) => requireArticle(post, true)) };
};
export const getManagedBlogPost = async (id, signal) => requireArticle(
  await request('get', `manage/posts/${encodeURIComponent(id)}/`, { signal, authenticated: true }), true
);
export const getManagedBlogPreview = async (id, signal) => {
  const data = await request('get', `manage/posts/${encodeURIComponent(id)}/preview/`, { signal, authenticated: true });
  if (data?.is_preview !== true || typeof data.is_publication_blocked !== 'boolean') throw failure('invalid_response');
  return { is_preview: true, is_publication_blocked: data.is_publication_blocked, post: requireArticle(data.post) };
};

const articlePayload = (form) => {
  if (Object.prototype.hasOwnProperty.call(form, 'tag_ids') && !validTagIds(form.tag_ids)) throw failure('validation', 400, ['tag_ids']);
  const payload = Object.fromEntries(editableFields
    .filter((field) => Object.prototype.hasOwnProperty.call(form, field))
    .map((field) => [field, form[field]]));
  if (!(form.cover_image instanceof File)) return payload;
  const multipart = new FormData();
  Object.entries(payload).forEach(([field, value]) => multipart.append(field, field === 'tag_ids' ? JSON.stringify(value) : value));
  multipart.append('cover_image', form.cover_image);
  return multipart;
};
export const saveBlogPost = async (id, form, signal) => requireArticle(await request(
  id ? 'patch' : 'post', id ? `manage/posts/${encodeURIComponent(id)}/` : 'manage/posts/',
  { data: articlePayload(form), signal, authenticated: true }
), true);
export const setBlogPublicationBlock = async (id, blocked, signal) => requireArticle(await request(
  'patch', `manage/posts/${encodeURIComponent(id)}/`,
  { data: { is_publication_blocked: blocked }, signal, authenticated: true }
), true);
export const setBlogArchive = async (id, archived, signal) => {
  if (!Number.isSafeInteger(id) || id < 1 || typeof archived !== 'boolean') throw failure('validation', 400);
  const data = await request('patch', `manage/posts/${id}/archive/`, { data: { archived }, signal, authenticated: true });
  if (!data || Object.keys(data).sort().join(',') !== 'archived,id' || data.id !== id || typeof data.archived !== 'boolean' || data.archived !== archived) throw failure('invalid_response');
  return { id: data.id, archived: data.archived };
};
